import { Link } from "react-router-dom";
import { MessageSquare, ClipboardList, Target, BadgeCheck, ArrowUpRight, ArrowRight, ScanLine } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { METHOD_STEPS } from "../data/method";
import { IMAGES, SERVICE_AREAS } from "../data/content";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";

const ICONS = { MessageSquare, ClipboardList, Target, BadgeCheck };

const DOMAIN = "https://www.speedyresurfacing.com.au";

export default function MethodIndex() {
  return (
    <div className="min-h-screen noise" data-testid="method-index">
      <Seo
        title="The Speedy Method | Court Resurfacing Sydney | Speedy Resurfacing"
        description="Our four-step court resurfacing method: consultation, preparation, execution and final inspection. The proven process behind premium court resurfacing and line marking in Sydney & NSW."
        path="/method"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HowTo",
              name: "The Speedy Method — court resurfacing in four steps",
              description:
                "The four-step process Speedy Resurfacing uses for court resurfacing and line marking across Sydney and NSW.",
              totalTime: "P7D",
              step: METHOD_STEPS.map((s, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: s.title,
                text: s.tagline,
                url: `${DOMAIN}/method/${s.slug}`,
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: DOMAIN + "/" },
                { "@type": "ListItem", position: 2, name: "The Speedy Method", item: DOMAIN + "/method" },
              ],
            },
          ],
        }}
      />

      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <img {...responsive(IMAGES.heroCourt, "100vw", [640, 1000, 1600])} decoding="async" alt="Court resurfacing Sydney — The Speedy Method" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/40" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <nav className="flex items-center gap-2 font-mono text-xs text-zinc-500 mb-6" data-testid="method-breadcrumb">
            <Link to="/" className="hover:text-neon">Home</Link><span>/</span>
            <span className="text-zinc-300">The Speedy Method</span>
          </nav>
          <Overline>Court Resurfacing · Line Marking Sydney</Overline>
          <h1 className="mt-5 font-heading font-black text-5xl md:text-7xl tracking-tighter max-w-4xl">
            FOUR STEPS. <span className="gold-text">ZERO GUESSWORK.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-300">
            The Speedy Method is the process behind every court we resurface — refined over years of court resurfacing and
            precision line marking across Sydney and NSW. Four steps, each one documented, each one you can read in full below.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 md:px-10 pb-24">
        {/* Step cards */}
        <div className="grid md:grid-cols-2 gap-5">
          {METHOD_STEPS.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.slug} delay={(i % 2) * 0.08}>
                <Link
                  to={`/method/${s.slug}`}
                  data-testid={`method-hub-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-lg border border-white/10 bg-obsidian-800 p-8 hover:border-gold/40 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-gold/10 text-gold group-hover:bg-gold group-hover:text-obsidian transition-colors">
                      <Icon size={24} />
                    </span>
                    <span className="font-heading font-black text-5xl text-zinc-800 group-hover:text-gold/40 transition-colors">{s.n}</span>
                  </div>
                  <h2 className="mt-7 font-heading font-bold text-2xl group-hover:text-neon transition-colors">{s.title}</h2>
                  <p className="mt-3 text-zinc-300 flex-1">{s.tagline}</p>
                  <span className="mt-5 font-mono text-[11px] uppercase tracking-widest text-zinc-500">{s.timeline}</span>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-bold text-gold group-hover:text-gold-hover">
                    Read step {s.n} in full
                    <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>

        {/* Why it matters */}
        <div className="mt-20 grid md:grid-cols-3 gap-5">
          {[
            { t: "Evidence, not assumptions", d: "Every recommendation traces back to something we measured on your court — cracking, drainage, coating adhesion, playable area." },
            { t: "Written before it is built", d: "Surface system, colours, layout and line marking are agreed in writing and itemised, so the finished court matches the one you signed off." },
            { t: "Signed off, not walked away from", d: "Nothing is complete until it has been inspected against Australian standards and walked with you at handover." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.06}>
              <div className="h-full rounded-lg border border-white/10 bg-obsidian-800 p-8">
                <h3 className="font-heading font-bold text-xl">{c.t}</h3>
                <p className="mt-3 text-sm text-zinc-300">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Service areas */}
        <div className="mt-20">
          <Reveal>
            <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight">
              Court resurfacing &amp; line marking across Sydney &amp; NSW
            </h2>
            <p className="mt-4 max-w-2xl text-zinc-400">
              The method is the same whether it is a backyard half-court, a school precinct, a club facility or a council asset.
            </p>
          </Reveal>
          <div className="mt-6 flex flex-wrap gap-2">
            {SERVICE_AREAS.map((a) => (
              <span key={a} className="rounded-sm border border-white/10 px-3 py-1.5 text-sm text-zinc-400">{a}</span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 grid md:grid-cols-2 gap-4">
          <Link to="/quote" data-testid="method-index-quote" className="flex items-center justify-between rounded-lg bg-neon p-8 text-obsidian shadow-neon group">
            <span className="font-heading font-black text-2xl">Start With Step One</span>
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={28} />
          </Link>
          <Link to="/court-scan" data-testid="method-index-scan" className="flex items-center justify-between rounded-lg border border-neon/30 bg-obsidian-800 p-8 text-white hover:border-neon transition-colors group">
            <span className="font-heading font-black text-2xl">Try Court Scan™</span>
            <ScanLine className="text-neon" size={28} />
          </Link>
        </div>
      </div>
    </div>
  );
}
